import { OmniaModuleId } from "../generated/enums"
import { omniaResolve } from "../promises"
import { IOmniaHttpService, OmniaHttpPostOptions } from "../types"
import { OmniaApiState } from "./apiState"
import { OmniaApiChange } from "./types"

export class OmniaApiInterface {

    constructor(
        private readonly ohttp: IOmniaHttpService,
        private readonly apiState: OmniaApiState,
        private readonly refresh: () => ng.IPromise<any>,
        config?: {
            source: OmniaModuleId
        },
        private readonly source = config?.source
    ) {
    }

    get<T>(url: string, blockUi?: boolean): ng.IPromise<T> {
        return this.ohttp.get(url, blockUi).then(r => r?.data)
    }

    /**
     * Sends a post request, registers a new change, and refreshes the service's data
     * @param url URL
     * @param data Post data
     * @param change Describes the change to register. If false, no change will be registered, and the service will not be refreshed.
     * @param refresh Indicates if the service should be refreshed after the change has been recorded
     */
    post(url: string, data?: any, change?: false | Omit<OmniaApiChange, "id" | "local">, refresh = true, blockUi = true) {

        const options: OmniaHttpPostOptions = {
            blockUi,
            changeApiVersion: false
        }

        let p = this.ohttp.post(url, data, false)

        return change === false
            ? p.then(r => r.data)
            : p.then(r => this.recordChange({ ...change, url }, refresh).then(() => r.data))

    }

    /**
     * Sends a put request, registers a new change, and refreshes the service's data
     * @param url URL
     * @param data Post data
     * @param change Describes the change to register. If false, no change will be registered, and the service will not be refreshed.
     * @param refresh Indicates if the service should be refreshed after the change has been recorded
     */
    put(url: string, data?: any, change?: false | Omit<OmniaApiChange, "id" | "local">, refresh = true) {

        let p = this.ohttp.put(url, data, false)

        p = change === false
            ? p.then(r => r.data)
            : p.then(r => this.recordChange({ ...change, url, action: change?.action || "c" }, refresh).then(() => r.data))

        return p

    }

    /**
     * Sends a delete request, registers a new change, and refreshes the service's data
     * @param url URL
     * @param data Delete data
     * @param change Describes the change to register. If false, no change will be registered, and the service will not be refreshed.
     * @param refresh Indicates if the service should be refreshed after the change has been recorded
     */
    delete(url: string, data?: any, change?: false | Omit<OmniaApiChange, "id" | "local">, refresh = true) {

        let p = this.ohttp.delete(url, data)

        return change === false
            ? p.then(r => r.data)
            : p.then(r => this.recordChange({ ...change, url, action: change?.action || "d" }, refresh).then(() => r.data))

    }

    /**
     * Records a new change and optionally refreshes the service's data
     * @param change Change
     * @param refresh Indicates if the service should be refresh
     */
    public recordChange(change?: Omit<OmniaApiChange, "id" | "local">, refresh = true) {

        this.apiState.recordChange({
            ...change,
            source: change?.source || this.source,
            local: true
        })

        return refresh
            ? this.refresh()
            : omniaResolve()

    }

}
